/**
 * Facilitator share-management data layer (multi-client plan).
 *
 * One query fetches the registry + every client's share manifest (≤20
 * clients, tiny JSON docs — a single fan-out is right-sized), inverted
 * into fast lookups for the per-file popover. The toggle mutation
 * rewrites ONE client's manifest via the strict BFF PUT and invalidates,
 * so enforcement and UI can't drift.
 *
 * Exact `path` entries are what the popover toggles. `prefix` (folder)
 * rules are surfaced read-only: a file covered by a folder rule shows as
 * shared-but-locked, since unticking it would need a manifest edit the
 * popover doesn't model. Right-sized: folder rules are authored rarely,
 * by hand, in the manifest itself.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getShareManifest,
  listClients,
  putShareManifest,
  type ClientEntry,
  type ShareEntry,
  type ShareManifest,
} from "@/lib/docs-api";

export interface ClientShareState {
  client: ClientEntry;
  /** Exact-path share exists for the file under inspection. */
  exact: boolean;
  /** A folder (prefix) rule covers it — display-only, not toggleable. */
  viaPrefix: boolean;
}

interface SharingData {
  clients: ClientEntry[];
  manifests: Record<string, ShareManifest>;
}

export function useSharing(enabled: boolean) {
  const queryClient = useQueryClient();

  const query = useQuery<SharingData>({
    queryKey: ["sharing"],
    enabled,
    queryFn: async () => {
      const clients = (await listClients()).filter((c) => c.status === "active");
      const manifests: Record<string, ShareManifest> = {};
      await Promise.all(
        clients.map(async (c) => {
          manifests[c.id] = await getShareManifest(c.id);
        }),
      );
      return { clients, manifests };
    },
  });

  const toggle = useMutation({
    mutationFn: async (args: {
      clientId: string;
      tier: string;
      path: string;
      share: boolean;
    }) => {
      const current =
        query.data?.manifests[args.clientId] ??
        (await getShareManifest(args.clientId));
      const without = current.shares.filter(
        (s) => !(s.tier === args.tier && s.path === args.path),
      );
      const next: ShareEntry[] = args.share
        ? [...without, { tier: args.tier, path: args.path }]
        : without;
      return await putShareManifest(args.clientId, next);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ["sharing"] }),
  });

  /** Share state of one file for every client (popover rows). */
  function statesFor(tier: string, path: string): ClientShareState[] {
    if (!query.data) return [];
    return query.data.clients.map((client) => {
      const shares = query.data!.manifests[client.id]?.shares ?? [];
      return {
        client,
        exact: shares.some((s) => s.tier === tier && s.path === path),
        viaPrefix: shares.some(
          (s) => s.tier === tier && !!s.prefix && path.startsWith(s.prefix),
        ),
      };
    });
  }

  /** How many clients can see this file (badge count on the row button). */
  function sharedCount(tier: string, path: string): number {
    return statesFor(tier, path).filter((s) => s.exact || s.viaPrefix).length;
  }

  return { query, toggle, statesFor, sharedCount };
}
